// COA (Chart of Accounts) — SAK EMKM untuk bisnis jasa servis HP. Logika MURNI (diuji), tanpa DOM.
// Pendekatan: COA-tagged reporting di atas data basis-kas yang ADA (bukan jurnal double-entry).
// Kode COA dipakai untuk memberi struktur & label standar pada Laba-Rugi & Neraca.

export type CoaTipe = 'aset' | 'liabilitas' | 'ekuitas' | 'pendapatan' | 'hpp' | 'beban';
export interface CoaAkun { kode: string; nama: string; tipe: CoaTipe }

/** Bagan akun ringkas (level yang dipakai laporan). Sub-akun kas dinamis dari cash_accounts. */
export const COA: CoaAkun[] = [
  { kode: '1-1', nama: 'Kas & Bank', tipe: 'aset' },
  { kode: '1-2', nama: 'Piutang usaha', tipe: 'aset' },
  { kode: '1-5', nama: 'Aset tetap', tipe: 'aset' },
  { kode: '1-6', nama: 'Akumulasi penyusutan', tipe: 'aset' }, // kontra-aset (nilai negatif)
  { kode: '2-1', nama: 'Hutang supplier', tipe: 'liabilitas' },
  { kode: '2-2', nama: 'Hutang beban', tipe: 'liabilitas' },
  { kode: '2-3', nama: 'DP pelanggan (diterima di muka)', tipe: 'liabilitas' },
  { kode: '3-1', nama: 'Modal pemilik', tipe: 'ekuitas' },
  { kode: '3-2', nama: 'Prive (pengambilan)', tipe: 'ekuitas' }, // kontra-ekuitas (negatif)
  { kode: '3-3', nama: 'Laba ditahan', tipe: 'ekuitas' },
  { kode: '3-4', nama: 'Laba tahun berjalan', tipe: 'ekuitas' },
  { kode: '4-1', nama: 'Pendapatan jasa servis', tipe: 'pendapatan' },
  { kode: '4-2', nama: 'Pendapatan lain-lain', tipe: 'pendapatan' },
  { kode: '5-1', nama: 'HPP sparepart', tipe: 'hpp' },
  { kode: '6-1', nama: 'Beban gaji & komisi', tipe: 'beban' },
  { kode: '6-2', nama: 'Beban sewa', tipe: 'beban' },
  { kode: '6-3', nama: 'Beban utilitas (listrik/air/internet)', tipe: 'beban' },
  { kode: '6-4', nama: 'Beban penyusutan', tipe: 'beban' },
  { kode: '6-5', nama: 'Beban hadiah & loyalti', tipe: 'beban' },
  { kode: '6-9', nama: 'Beban lain-lain', tipe: 'beban' },
];

/** Tipe akun dari digit pertama kode (1=aset,2=liabilitas,3=ekuitas,4=pendapatan,5=hpp,6=beban). */
export function tipeFromKode(kode: string): CoaTipe {
  switch ((kode || '').trim()[0]) {
    case '1': return 'aset';
    case '2': return 'liabilitas';
    case '3': return 'ekuitas';
    case '4': return 'pendapatan';
    case '5': return 'hpp';
    default: return 'beban';
  }
}

/** Gabung COA bawaan + akun buatan pengguna (custom menang bila kode sama). Urut per kode. */
export function mergeCoa(custom: { kode: string; nama: string }[]): CoaAkun[] {
  const map = new Map<string, CoaAkun>();
  for (const c of COA) map.set(c.kode, c);
  for (const c of custom) { const kode = (c.kode || '').trim(); if (kode && c.nama?.trim()) map.set(kode, { kode, nama: c.nama.trim(), tipe: tipeFromKode(kode) }); }
  return [...map.values()].sort((a, b) => a.kode.localeCompare(b.kode, undefined, { numeric: true }));
}
/** Akun bawaan (tak boleh dihapus — terhubung ke data). */
export const isCoaBawaan = (kode: string): boolean => COA.some((c) => c.kode === kode);

/** Peta kategori beban (BEBAN_KAT) → kode COA 6-x. Cocokkan kata kunci; sisanya → 6-9. */
export function bebanKodeCoa(kategori: string): string {
  const k = (kategori || '').toLowerCase();
  if (/gaji|komisi|upah|bonus|thr|karyawan/.test(k)) return '6-1';
  if (/sewa|kontrak/.test(k)) return '6-2';
  if (/listrik|air|internet|utilitas|telepon|pulsa|wifi/.test(k)) return '6-3';
  if (/susut|penyusutan|depresiasi/.test(k)) return '6-4';
  if (/hadiah|loyalti|doorprize|undian|promo|marketing|iklan/.test(k)) return '6-5';
  return '6-9';
}
export const coaNama = (kode: string): string => COA.find((c) => c.kode === kode)?.nama ?? kode;

export interface BarisLaporan { kode: string; nama: string; nilai: number }

/** Angka mentah untuk menyusun Neraca (semua sudah basis-kas, satu entitas/konsolidasi). */
export interface NeracaInput {
  kasBank: number; piutang: number; asetTetap: number; akmPenyusutan: number; // aset (akm negatif)
  hutangSupplier: number; hutangBeban: number; dpPelanggan: number;           // liabilitas
  modal: number; prive: number; labaDitahan: number; labaBerjalan: number;    // ekuitas (prive negatif)
}
export interface Neraca {
  aset: BarisLaporan[]; totalAset: number;
  liabilitas: BarisLaporan[]; totalLiabilitas: number;
  ekuitas: BarisLaporan[]; totalEkuitas: number;
  totalKanan: number;      // liabilitas + ekuitas
  selisih: number;         // aset − (liabilitas+ekuitas); idealnya 0
  seimbang: boolean;
}

/** Baris tambahan buatan pengguna (saldo manual) yang disisipkan ke grup sesuai kode. */
export interface ExtraRow { kode: string; nama: string; nilai: number }

/** Susun Neraca berformat COA + cek keseimbangan (Aset = Liabilitas + Ekuitas). `extra` = akun custom
 *  bersaldo manual, disisipkan ke ASET/LIABILITAS/EKUITAS menurut digit pertama kodenya. */
export function susunNeraca(f: NeracaInput, extra: ExtraRow[] = []): Neraca {
  const aset: BarisLaporan[] = [
    { kode: '1-1', nama: coaNama('1-1'), nilai: f.kasBank },
    { kode: '1-2', nama: coaNama('1-2'), nilai: f.piutang },
    { kode: '1-5', nama: coaNama('1-5'), nilai: f.asetTetap },
  ];
  if (f.akmPenyusutan) aset.push({ kode: '1-6', nama: coaNama('1-6'), nilai: -Math.abs(f.akmPenyusutan) });
  const liabilitas: BarisLaporan[] = [
    { kode: '2-1', nama: coaNama('2-1'), nilai: f.hutangSupplier },
    { kode: '2-2', nama: coaNama('2-2'), nilai: f.hutangBeban },
    { kode: '2-3', nama: coaNama('2-3'), nilai: f.dpPelanggan },
  ];
  const ekuitas: BarisLaporan[] = [
    { kode: '3-1', nama: coaNama('3-1'), nilai: f.modal },
    { kode: '3-2', nama: coaNama('3-2'), nilai: -Math.abs(f.prive) },
    { kode: '3-3', nama: coaNama('3-3'), nilai: f.labaDitahan },
    { kode: '3-4', nama: coaNama('3-4'), nilai: f.labaBerjalan },
  ];
  // Akun custom (saldo manual) → grup sesuai kode. 4/5/6 (P&L) diabaikan di Neraca.
  for (const e of extra) {
    if (!e.nilai) continue;
    const t = tipeFromKode(e.kode);
    if (t === 'aset') aset.push({ kode: e.kode, nama: e.nama, nilai: e.nilai });
    else if (t === 'liabilitas') liabilitas.push({ kode: e.kode, nama: e.nama, nilai: e.nilai });
    else if (t === 'ekuitas') ekuitas.push({ kode: e.kode, nama: e.nama, nilai: e.nilai });
  }
  aset.sort((a, b) => a.kode.localeCompare(b.kode, undefined, { numeric: true }));
  liabilitas.sort((a, b) => a.kode.localeCompare(b.kode, undefined, { numeric: true }));
  ekuitas.sort((a, b) => a.kode.localeCompare(b.kode, undefined, { numeric: true }));
  const totalAset = aset.reduce((a, b) => a + b.nilai, 0);
  const totalLiabilitas = liabilitas.reduce((a, b) => a + b.nilai, 0);
  const totalEkuitas = ekuitas.reduce((a, b) => a + b.nilai, 0);
  const totalKanan = totalLiabilitas + totalEkuitas;
  const selisih = Math.round(totalAset - totalKanan);
  return { aset, totalAset, liabilitas, totalLiabilitas, ekuitas, totalEkuitas, totalKanan, selisih, seimbang: selisih === 0 };
}
